import React, { useState } from 'react'

// §8.6 NumericKeypadInput
// - 最小點擊區 48×48 px
// - 單位固定顯示在框右側，不可切換（§13.7 數字一律 kg）
// - 輸入後即時顯示綠／黃／紅（通過／soft limit 警示／hard limit 攔阻）

export type LimitState = 'ok' | 'soft' | 'hard' | 'empty'

export function evaluateLimits(
  value: number | null,
  soft?: { min: number | null; max: number | null },
  hard?: { min: number | null; max: number | null },
): LimitState {
  if (value === null || Number.isNaN(value)) return 'empty'
  if (hard) {
    if (hard.min !== null && value < hard.min) return 'hard'
    if (hard.max !== null && value > hard.max) return 'hard'
  }
  if (soft) {
    if (soft.min !== null && value < soft.min) return 'soft'
    if (soft.max !== null && value > soft.max) return 'soft'
  }
  return 'ok'
}

export function NumericKeypadInput({
  value, onChange, unit = 'kg', decimals = 2, autoFocus, onEnter,
  soft, hard, keypad = false, placeholder, disabled, inputRef, className = '', dataIdx,
}: {
  value: string
  onChange: (v: string) => void
  unit?: string
  decimals?: number
  autoFocus?: boolean
  onEnter?: () => void
  soft?: { min: number | null; max: number | null }
  hard?: { min: number | null; max: number | null }
  keypad?: boolean
  placeholder?: string
  disabled?: boolean
  inputRef?: React.RefObject<HTMLInputElement>
  className?: string
  /** 逐片輸入時用來做 Enter 跳下一片 */
  dataIdx?: number
}) {
  const [showPad, setShowPad] = useState(false)
  const num = value.trim() === '' ? null : Number(value)
  const state = evaluateLimits(num, soft, hard)
  const ring = state === 'hard' ? 'border-red-500 bg-red-50 text-red-900'
    : state === 'soft' ? 'border-amber-500 bg-amber-50 text-amber-900'
      : state === 'ok' ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
        : 'border-ink-300 bg-white text-ink-900'

  const push = (d: string) => {
    if (d === 'C') return onChange('')
    if (d === '←') return onChange(value.slice(0, -1))
    if (d === '.' && value.includes('.')) return
    onChange(value + d)
  }

  return (
    <div className={className}>
      <div className={`flex items-stretch rounded-md border ${ring} focus-within:ring-2 focus-within:ring-sky-500 overflow-hidden`}>
        <input
          ref={inputRef}
          data-weigh-idx={dataIdx}
          type="text"
          inputMode="decimal"
          disabled={disabled}
          autoFocus={autoFocus}
          placeholder={placeholder ?? (0).toFixed(decimals)}
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^\d.\-]/g, ''))}
          onKeyDown={(e) => { if (e.key === 'Enter' && onEnter) { e.preventDefault(); onEnter() } }}
          className="flex-1 min-w-0 bg-transparent px-3 py-2 text-right font-mono text-base tabular-nums focus:outline-none disabled:text-ink-400"
        />
        <span className="flex items-center px-2 text-xs text-ink-500 border-l border-ink-200 bg-white/60 select-none">{unit}</span>
        {keypad && (
          <button
            type="button"
            onClick={() => setShowPad((s) => !s)}
            className="px-3 border-l border-ink-200 bg-white/60 text-ink-500 hover:bg-ink-100 min-w-touch"
            aria-label="開啟數字鍵盤"
          >⌨</button>
        )}
      </div>
      {state === 'hard' && <p className="text-[11px] text-red-700 mt-1">超出硬性上下限，無法過帳</p>}
      {state === 'soft' && <p className="text-[11px] text-amber-700 mt-1">超出建議範圍，請確認後再送出</p>}
      {keypad && showPad && (
        <div className="mt-2 grid grid-cols-3 gap-1.5 max-w-[220px]">
          {['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0', '←'].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => push(d)}
              className="min-h-touch min-w-touch rounded-md border border-ink-300 bg-white text-lg font-medium text-ink-800 hover:bg-ink-100 active:bg-ink-200"
            >{d}</button>
          ))}
          <button
            type="button"
            onClick={() => push('C')}
            className="col-span-3 min-h-touch rounded-md border border-ink-300 bg-ink-50 text-sm text-ink-700 hover:bg-ink-100"
          >清除</button>
        </div>
      )}
    </div>
  )
}
